import { useState } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
} from "recharts";
import "./dashboard.css";

// ─── Stub data ─────────────────────────────────────────────────────────────────

const TICKERS = ["TSLA", "MSFT", "IBM", "AAPL"];
const LAGS = Array.from({ length: 17 }, (_, i) => i - 8);

const HEATMAP = {
  TSLA: [0.12, 0.22, 0.38, 0.52, 0.65, 0.72, 0.60, 0.44, 0.28, 0.16, 0.08, -0.04, -0.14, -0.20, -0.24, -0.28, -0.10],
  MSFT: [0.06, 0.16, 0.30, 0.44, 0.56, 0.62, 0.54, 0.40, 0.24, 0.12, 0.04, -0.02, -0.10, -0.18, -0.22, -0.14, -0.06],
  IBM:  [-0.22, -0.12, 0.04, 0.18, 0.28, 0.32, 0.24, 0.14, 0.04, -0.06, -0.12, -0.20, -0.26, -0.30, -0.22, -0.12, -0.06],
  AAPL: [0.18, 0.32, 0.46, 0.60, 0.70, 0.72, 0.62, 0.48, 0.32, 0.18, 0.06, -0.04, -0.14, -0.22, -0.28, -0.30, -0.20],
};

function corrColor(r) {
  const alpha = (0.15 + Math.abs(r) * 0.85).toFixed(2);
  return r >= 0
    ? `rgba(0, 200, 157, ${alpha})`
    : `rgba(255, 94, 101, ${alpha})`;
}

const ROLLING_DATA = [
  { month: "Mar'23", sharpe: 0.32 },
  { month: "May'23", sharpe: 0.88 },
  { month: "Jul'23", sharpe: 1.42 },
  { month: "Sep'23", sharpe: 1.78 },
  { month: "Nov'23", sharpe: 0.55 },
  { month: "Jan'24", sharpe: -0.08 },
  { month: "Mar'24", sharpe: 0.94 },
  { month: "May'24", sharpe: 2.02 },
];

const mk = (pts) => pts.map((value, i) => ({ i, value }));

const TEAR_SHEET = [
  {
    title: "Returns Summary",
    metrics: [
      { label: "Annualized Return",  value: "+18.4%", color: "#00c89d", data: mk([0.10, 0.14, 0.16, 0.18, 0.17, 0.19, 0.18, 0.184]) },
      { label: "Cumulative Return",  value: "+62.1%", color: "#00c89d", data: mk([0.05, 0.13, 0.24, 0.35, 0.44, 0.52, 0.58, 0.621]) },
      { label: "Best Month",         value: "+9.3%",  color: "#00c89d", data: mk([0.03, 0.07, 0.09, 0.06, 0.08, 0.09, 0.08, 0.093]) },
      { label: "Worst Month",        value: "-6.1%",  color: "#ff5e65", data: mk([-0.02, -0.04, -0.06, -0.04, -0.05, -0.06, -0.05, -0.061]) },
      { label: "% Positive Months",  value: "61%",    color: "#ffffff", data: mk([0.55, 0.58, 0.60, 0.62, 0.60, 0.61, 0.63, 0.61]) },
    ],
  },
  {
    title: "Risk Metrics",
    metrics: [
      { label: "Annualized Volatility", value: "14.2%",  color: "#ffffff", data: mk([12, 13, 15, 14, 13, 15, 14.5, 14.2]) },
      { label: "Max Drawdown",          value: "-11.8%", color: "#ff5e65", data: mk([-5, -8, -11, -9, -10, -12, -11, -11.8]) },
      { label: "Drawdown Duration",     value: "4 mo",   color: "#ffffff", data: mk([1, 2, 3, 4, 3, 4, 4, 4]) },
      { label: "Value at Risk (95%)",   value: "-2.3%",  color: "#ff5e65", data: mk([-1.5, -1.8, -2.0, -2.1, -2.2, -2.3, -2.2, -2.3]) },
      { label: "Skewness",              value: "-0.42",  color: "#ff5e65", data: mk([-0.1, -0.2, -0.3, -0.4, -0.35, -0.42, -0.4, -0.42]) },
    ],
  },
  {
    title: "Risk-adjusted Ratios",
    metrics: [
      { label: "Sharpe Ratio",      value: "1.29",  color: "#00c89d", data: mk([0.3, 0.6, 0.9, 1.1, 0.9, 1.2, 1.3, 1.29]) },
      { label: "Sortino Ratio",     value: "1.87",  color: "#00c89d", data: mk([0.5, 0.9, 1.2, 1.5, 1.4, 1.7, 1.8, 1.87]) },
      { label: "Calmar Ratio",      value: "1.56",  color: "#00c89d", data: mk([0.4, 0.8, 1.1, 1.4, 1.3, 1.5, 1.6, 1.56]) },
      { label: "Information Ratio", value: "0.84",  color: "#ffffff", data: mk([0.3, 0.5, 0.6, 0.7, 0.8, 0.85, 0.84, 0.84]) },
      { label: "Treynor Ratio",     value: "-0.04", color: "#ff5e65", data: mk([-0.01, -0.02, -0.03, -0.04, -0.03, -0.04, -0.04, -0.04]) },
    ],
  },
  {
    title: "Benchmark Comparison",
    metrics: [
      { label: "Alpha (annualized)",    value: "+7.2%",  color: "#00c89d", data: mk([2, 4, 5.5, 6.8, 7.0, 7.2, 7.1, 7.2]) },
      { label: "Beta",                  value: "0.61",   color: "#ffffff", data: mk([0.7, 0.65, 0.62, 0.60, 0.61, 0.61, 0.61, 0.61]) },
      { label: "Correlation (S&P 500)", value: "0.48",   color: "#ffffff", data: mk([0.3, 0.4, 0.45, 0.48, 0.47, 0.48, 0.48, 0.48]) },
      { label: "Active Return",         value: "+10.1%", color: "#00c89d", data: mk([2, 5, 7, 9, 9.5, 10, 10.1, 10.1]) },
      { label: "Tracking Error",        value: "12.0%",  color: "#ffffff", data: mk([10, 11, 11.5, 12, 12, 12, 12, 12]) },
    ],
  },
];

// ─── Sub-components ────────────────────────────────────────────────────────────

function Sparkline({ data, color }) {
  return (
    <LineChart width={100} height={36} data={data}>
      <Line
        type="monotone"
        dataKey="value"
        stroke={color}
        strokeWidth={1.5}
        dot={false}
        isAnimationActive={false}
      />
    </LineChart>
  );
}

function MetricRow({ label, value, color, data, isLast }) {
  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      gap: "8px",
      paddingBottom: isLast ? 0 : "14px",
      marginBottom: isLast ? 0 : "14px",
      borderBottom: isLast ? "none" : "1px solid rgba(255,255,255,0.06)",
    }}>
      <span style={{ fontSize: "12px", color: "#aeaeae", flex: "1 1 0", minWidth: 0 }}>{label}</span>
      <Sparkline data={data} color={color} />
      <span style={{ fontSize: "14px", fontWeight: 700, color, minWidth: "56px", textAlign: "right" }}>{value}</span>
    </div>
  );
}

function TearPanel({ title, metrics }) {
  return (
    <div style={{
      background: "rgba(73, 73, 73, 0.4)",
      borderRadius: "16px",
      padding: "20px",
    }}>
      <p style={{ fontSize: "13px", fontWeight: 600, color: "#ffffff", margin: "0 0 20px 0" }}>{title}</p>
      {metrics.map((m, i) => (
        <MetricRow key={m.label} {...m} isLast={i === metrics.length - 1} />
      ))}
    </div>
  );
}

function SectionHeader({ main, sub }) {
  return (
    <div style={{ marginBottom: "8px" }}>
      <span style={{ fontSize: "14px", fontWeight: 700, color: "#ffffff" }}>{main}</span>
      {sub && (
        <span style={{ fontSize: "14px", fontWeight: 400, color: "rgba(255,255,255,0.5)" }}>
          {" / "}{sub}
        </span>
      )}
    </div>
  );
}

const chipBase = {
  background: "rgba(73, 73, 73, 0.6)",
  borderRadius: "20px",
  padding: "6px 14px",
  display: "inline-flex",
  flexDirection: "column",
  gap: "2px",
};

// ─── Main Component ────────────────────────────────────────────────────────────

export default function FactorResearch() {
  const [rollingWindow, setRollingWindow] = useState("12M");

  const STAT_CHIPS = [
    { label: "peak lrl",     value: "0.724",     valueColor: "#ffffff" },
    { label: "at lag",       value: "undefined", valueColor: "#aeaeae" },
    { label: "data min r",   value: "−0.724",    valueColor: "#ff5e65" },
    { label: "data max r",   value: "+0.486",    valueColor: "#00c89d" },
    { label: "positive lags", value: "16",       valueColor: "#00c89d" },
    { label: "negative lags", value: "23",       valueColor: "#ff5e65" },
  ];

  return (
    <div className="db-content">

      {/* ── Section 1: Cross-Correlation Heatmap ── */}
      <div className="db-card" style={{ width: "fit-content", minWidth: 0, alignSelf: "flex-start" }}>
        <SectionHeader main="Cross-Correlation Heatmap" sub="Lag Sweep" />
        <p style={{ fontSize: "13px", color: "#a4a7ae", margin: "0 0 16px 0" }}>
          Darker Green → Positive &nbsp;|&nbsp; Darker Red → Negative
        </p>

        <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "20px" }}>
          {STAT_CHIPS.map(({ label, value, valueColor }) => (
            <div key={label} style={chipBase}>
              <span style={{ fontSize: "10px", color: "#737373", lineHeight: "14px" }}>{label}</span>
              <span style={{ fontSize: "13px", fontWeight: 600, color: valueColor, lineHeight: "18px" }}>{value}</span>
            </div>
          ))}
        </div>

        <div style={{ overflowX: "auto", width: "fit-content", display: "inline-block" }}>
          {TICKERS.map((ticker) => (
            <div key={ticker} style={{ display: "flex", alignItems: "center", gap: "6px", marginBottom: "4px" }}>
              <span style={{ width: "42px", fontSize: "12px", color: "#a0a0a8", flexShrink: 0 }}>{ticker}</span>
              <div style={{ display: "flex", gap: "3px" }}>
                {HEATMAP[ticker].map((r, i) => (
                  <div
                    key={i}
                    title={`lag ${LAGS[i]}: ${r.toFixed(2)}`}
                    style={{
                      width: "28px",
                      height: "40px",
                      borderRadius: "4px",
                      background: corrColor(r),
                      flexShrink: 0,
                    }}
                  />
                ))}
              </div>
            </div>
          ))}

          <div style={{ display: "flex", gap: "3px", marginLeft: "48px", marginTop: "4px" }}>
            {LAGS.map((lag) => (
              <div key={lag} style={{ width: "28px", textAlign: "center", fontSize: "10px", color: "#737373", flexShrink: 0 }}>
                {lag}
              </div>
            ))}
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "14px", maxWidth: "360px" }}>
          <span style={{ fontSize: "11px", color: "#737373" }}>−1</span>
          <div style={{
            flex: 1,
            height: "10px",
            borderRadius: "5px",
            background: "linear-gradient(to right, #ff5e65, rgba(73,73,73,0.6), #00c89d)",
          }} />
          <span style={{ fontSize: "11px", color: "#737373" }}>1</span>
        </div>
      </div>

      {/* ── Section 2: Rolling Window + Chow Break Summary ── */}
      <div style={{ display: "grid", gridTemplateColumns: "793fr 555fr", gap: "14px" }}>

        {/* Left: Rolling Window Chart */}
        <div className="db-card">
          <SectionHeader main="Rolling Window & Chow Structural-Break Test" />
          <p style={{ fontSize: "13px", color: "#a4a7ae", margin: "0 0 14px 0" }}>36-month rolling Sharpe</p>

          <div style={{ display: "flex", gap: "6px", marginBottom: "16px" }}>
            {["12M", "24M", "36M"].map((w) => (
              <button
                key={w}
                onClick={() => setRollingWindow(w)}
                style={{
                  background: rollingWindow === w ? "rgba(255,255,255,0.12)" : "transparent",
                  border: `1px solid ${rollingWindow === w ? "rgba(255,255,255,0.2)" : "rgba(255,255,255,0.08)"}`,
                  borderRadius: "20px",
                  color: rollingWindow === w ? "#ffffff" : "#737373",
                  fontSize: "12px",
                  fontWeight: rollingWindow === w ? 600 : 400,
                  padding: "4px 14px",
                  cursor: "pointer",
                }}
              >
                {w}
              </button>
            ))}
          </div>

          <div style={{ height: "220px", marginBottom: "20px" }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={ROLLING_DATA} margin={{ top: 4, right: 20, left: -20, bottom: 4 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="month"
                  tick={{ fill: "#737373", fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  tickMargin={8}
                />
                <YAxis
                  domain={[-0.5, 2.0]}
                  ticks={[-0.5, 0, 0.5, 1.0, 1.5, 2.0]}
                  tick={{ fill: "#737373", fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <ReferenceLine x="Sep'23" stroke="#ff5e65" strokeDasharray="4 3" strokeWidth={1.5} />
                <ReferenceLine x="Jan'24" stroke="#ff5e65" strokeDasharray="4 3" strokeWidth={1.5} />
                <Line
                  type="monotone"
                  dataKey="sharpe"
                  stroke="#5da1ff"
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <p style={{ fontSize: "13px", fontWeight: 600, color: "#ffffff", margin: "0 0 10px 0" }}>
            Chow test results
          </p>
          <div style={{ display: "flex", gap: "16px", flexWrap: "wrap", width: "100%" }}>
            {[
              { label: "F-stat",       value: "8.41",  color: "#ffffff" },
              { label: "Breaks found", value: "2",     color: "#ffffff" },
              { label: "p-value",      value: "0.018", color: "#ff5e65" },
            ].map(({ label, value, color }) => (
              <div key={label} style={{ ...chipBase, flex: 1, minWidth: "120px", padding: "16px 20px" }}>
                <span style={{ fontSize: "10px", color: "#737373", lineHeight: "14px" }}>{label}</span>
                <span style={{ fontSize: "13px", fontWeight: 600, color, lineHeight: "18px" }}>{value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right: Chow Break Summary */}
        <div className="db-card">
          <SectionHeader main="Chow Break Summary" />
          <p style={{ fontSize: "13px", color: "#a4a7ae", margin: "0 0 20px 0" }}>Structural break statistics</p>

          {[
            { regime: "Regime 1", sharpe: "0.41",  sharpeColor: "#ffffff", vol: "11.2%" },
            { regime: "Regime 2", sharpe: "1.29",  sharpeColor: "#00c89d", vol: "14.2%" },
          ].map(({ regime, sharpe, sharpeColor, vol }) => (
            <div
              key={regime}
              style={{
                background: "rgba(73, 73, 73, 0.5)",
                borderRadius: "16px",
                padding: "16px 20px",
                marginBottom: "12px",
              }}
            >
              <p style={{ fontSize: "12px", color: "#737373", margin: "0 0 12px 0" }}>{regime}</p>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
                <div>
                  <p style={{ fontSize: "11px", color: "#737373", margin: "0 0 4px 0" }}>Average Sharpe</p>
                  <p style={{ fontSize: "22px", fontWeight: 700, color: sharpeColor, margin: 0 }}>{sharpe}</p>
                </div>
                <div style={{ textAlign: "right" }}>
                  <p style={{ fontSize: "11px", color: "#737373", margin: "0 0 4px 0" }}>Volatility</p>
                  <p style={{ fontSize: "22px", fontWeight: 700, color: "#ffffff", margin: 0 }}>{vol}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Section 3: Rolling Window Tear Sheet ── */}
      <div className="db-card">
        <SectionHeader main="Rolling Window Tear Sheet" sub="36-Month Window" />
        <p style={{ fontSize: "13px", color: "#a4a7ae", margin: "0 0 20px 0" }}>
          Key performance metrics across rolling periods
        </p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
          {TEAR_SHEET.map((panel) => (
            <TearPanel key={panel.title} {...panel} />
          ))}
        </div>
      </div>

    </div>
  );
}
